import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Block } from "@/types/proposals";

interface PackageSelectorBlockProps {
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
  isTemplate?: boolean;
  proposalId?: string;
}

export function PackageSelectorBlock({ block, onUpdate, isTemplate, proposalId }: PackageSelectorBlockProps) {
  const [packages, setPackages] = useState<any[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const packageGroupIds = block.settings?.packageGroupIds || [];
  const displayStyle = block.settings?.displayStyle || 'good_better_best';
  const syncMode = block.settings?.syncMode || 'locked';
  const visibilitySettings = block.settings?.visibilitySettings || {
    showUnit: true,
    showQuantity: true,
    showUnitPrice: true,
    showLineItemTotal: true,
    showOnlyTotal: false,
    hiddenLineItemIds: [],
    layoutStyle: 'table'
  };

  // Filter visible items
  const getVisibleItems = (items: any[]) => {
    return items.filter(item => 
      !visibilitySettings.hiddenLineItemIds.includes(item.id || item.name)
    );
  };

  useEffect(() => {
    loadPackages();
  }, [packageGroupIds, proposalId, syncMode]);

  const loadPackages = async () => {
    if (packageGroupIds.length === 0) {
      setLoading(false);
      return;
    }

    try {
      if (proposalId && syncMode === 'locked') {
        // Load from snapshots
        const { data, error } = await supabase
          .from('proposal_package_snapshots')
          .select('*')
          .eq('proposal_document_id', proposalId)
          .in('package_group_id', packageGroupIds);

        if (error) throw error;

        setPackages(data?.map(snap => {
          const snapshotData = snap.snapshot_data as any;
          return {
            id: snap.id,
            package_group_id: snap.package_group_id,
            ...snapshotData,
            selected: snap.selected_by_customer
          };
        }) || []);

        const selected = data?.find(s => s.selected_by_customer);
        if (selected) setSelectedPackageId(selected.id);
      } else {
        // Load live from package_groups
        const { data, error } = await supabase
          .from('package_groups')
          .select(`
            *,
            package_group_items(
              *,
              catalog_item:catalog_items(*)
            )
          `)
          .in('id', packageGroupIds);

        if (error) throw error;

        setPackages(data?.map(pkg => ({
          id: pkg.id,
          name: pkg.name,
          description: pkg.description,
          items: pkg.package_group_items?.map((item: any) => ({
            name: item.catalog_item?.name,
            quantity: item.quantity,
            unit_price: item.catalog_item?.unit_price || 0,
            total: item.quantity * (item.catalog_item?.unit_price || 0)
          })) || [],
          total_price: pkg.package_group_items?.reduce(
            (sum: number, item: any) => sum + (item.quantity * (item.catalog_item?.unit_price || 0)),
            0
          ) || 0
        })) || []);
      }
    } catch (error) {
      console.error('Error loading packages:', error);
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPackage = async (packageId: string) => {
    if (isTemplate || !proposalId) return;

    try {
      // Update all snapshots to unselect
      await supabase
        .from('proposal_package_snapshots')
        .update({ selected_by_customer: false })
        .eq('proposal_document_id', proposalId);

      // Select this one
      await supabase
        .from('proposal_package_snapshots')
        .update({ selected_by_customer: true })
        .eq('id', packageId);

      setSelectedPackageId(packageId);
      toast.success("Package selected");

      // Notify parent to update totals
      onUpdate({
        content: {
          ...block.content,
          selectedPackageId: packageId
        }
      });
    } catch (error) {
      console.error('Error selecting package:', error);
      toast.error("Failed to select package");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">Loading packages...</p>
      </Card>
    );
  }

  if (packages.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-muted-foreground">
          {isTemplate ? "Select packages in settings" : "No packages available"}
        </p>
      </Card>
    );
  }

  if (displayStyle === 'good_better_best') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {packages.slice(0, 3).map((pkg, idx) => {
          const isSelected = pkg.id === selectedPackageId || pkg.selected;
          const labels = ['Good', 'Better', 'Best'];
          
          return (
            <Card
              key={pkg.id}
              className={`p-6 cursor-pointer transition-all ${
                isSelected ? 'border-primary border-2 shadow-lg' : 'hover:shadow-md'
              }`}
              onClick={() => handleSelectPackage(pkg.id)}
            >
              <div className="text-center">
                <Badge variant={idx === 2 ? 'default' : 'secondary'} className="mb-4">
                  {labels[idx]}
                </Badge>
                {isSelected && (
                  <div className="absolute top-4 right-4">
                    <Check className="w-6 h-6 text-primary" />
                  </div>
                )}
                <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                <div className="text-3xl font-bold text-primary mb-6">
                  ${pkg.total_price?.toLocaleString() || '0'}
                </div>
                {!visibilitySettings.showOnlyTotal && (
                  <div className="space-y-2 text-left">
                    {getVisibleItems(pkg.items || []).slice(0, 5).map((item: any, i: number) => (
                      <div key={i} className={visibilitySettings.layoutStyle === 'stacked' ? 'space-y-1' : 'text-sm'}>
                        {visibilitySettings.layoutStyle === 'stacked' ? (
                          <>
                            <div className="font-medium">{item.name}</div>
                            {item.description && <div className="text-xs text-muted-foreground">{item.description}</div>}
                            <div className="flex justify-between text-sm">
                              {visibilitySettings.showQuantity && <span>Qty: {item.quantity}</span>}
                              {visibilitySettings.showUnitPrice && <span>${item.unit_price}</span>}
                            </div>
                          </>
                        ) : (
                          <>
                            <span>• {item.name}</span>
                            {visibilitySettings.showQuantity && <span> ({item.quantity})</span>}
                            {visibilitySettings.showUnitPrice && <span> - ${item.unit_price}</span>}
                          </>
                        )}
                      </div>
                    ))}
                    {getVisibleItems(pkg.items || []).length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        +{getVisibleItems(pkg.items || []).length - 5} more items
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-4">
      {packages.map((pkg) => {
        const isSelected = pkg.id === selectedPackageId || pkg.selected;
        
        return (
          <Card
            key={pkg.id}
            className={`p-6 cursor-pointer transition-all ${
              isSelected ? 'border-primary border-2' : 'hover:shadow-md'
            }`}
            onClick={() => handleSelectPackage(pkg.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold">{pkg.name}</h3>
                  {isSelected && <Check className="w-5 h-5 text-primary" />}
                </div>
                <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {pkg.items?.map((item: any, i: number) => (
                    <div key={i} className="text-sm">
                      • {item.name} x{item.quantity}
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-primary">
                  ${pkg.total_price?.toLocaleString() || '0'}
                </div>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
